/**
 * `+ New organization` — provisioning a customer from the console (phase 6, D3).
 *
 * ── Why the console needed its own create at all ────────────────────────
 *
 * Every organization on this deployment had to be created by its own owner,
 * self-service, under the free-org limit. An operator setting a customer up on
 * a call had to talk them through signing up, then find the new tenant and
 * configure it from three separate screens. This is the create half of "the
 * customer is the object".
 *
 * ── The one thing this form must be honest about ────────────────────────
 *
 * It does not mint an account. If the owner's address already has one they are
 * seated immediately; if not, an invite waits for them to consent and choose
 * their own password. The operator is told WHICH happened, because "created and
 * waiting" and "created and in" are different states and only one of them needs
 * a follow-up. The form says so before submit, not only after.
 *
 * ── Limits are the plan's job, not the create form's ────────────────────
 *
 * This form no longer edits per-tenant ceilings. A new customer takes whatever
 * their plan grants; the four cap inputs (budget, overage, concurrency, quota)
 * moved off here entirely, and are set — if ever — on the tenant's Plan tab
 * after creation. Creating a customer is choosing a plan, not hand-tuning caps.
 *
 * ── No plan selector, but the form NAMES where the customer starts ─────
 *
 * There is still no dropdown, and for two different reasons depending on how
 * this deployment decides plans:
 *
 *   provider  → a paid plan here would write a divergence from Stripe without
 *               the marker, the audit action or the reconciliation that decision
 *               D1 builds for it. A tenant with no subscription IS on free.
 *   manual    → the tenant is seeded from the deployment's DEFAULT PRESET,
 *               server-side. A per-creation dropdown on a queue of
 *               near-identical customers is how the wrong plan gets picked; one
 *               pointer set once in Platform settings is the safer shape, and
 *               any single customer can be moved afterwards.
 *
 * What it must NOT do is keep saying "starts on the free plan" while the server
 * seeds from something else — the form would then be describing a deployment
 * this one is not. So it reads the pointer and names the preset, and says so
 * plainly when none is configured, because that case seeds from `free` and the
 * free plan usually carries a zero AI budget.
 */
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from '@papan/i18n'
import { Modal, useToast } from '@papan/core-ui/ui'
import { SUPPORTED_DISPLAY_CURRENCIES } from '@papan/types'
import { budgetApi } from '../../client/budget'
import { InfoHint } from './bits'

export function NewCustomerDialog({
  onCreated, onClose,
}: {
  /** Called with the new tenant id so the caller can open it — provisioning
   *  and then having to find the customer in a list would undo the point. */
  onCreated: (tenantId: string) => void
  onClose: () => void
}) {
  const { t } = useTranslation('core-ui')
  // The theme class, the focus move, the focus trap, Escape and `inert` on the
  // background are all `Modal`'s job now — see its header for why they were
  // worth centralising rather than repeating.
  const toast = useToast()
  const nameRef = useRef<HTMLInputElement>(null)

  const [name, setName] = useState('')
  const [ownerEmail, setOwnerEmail] = useState('')
  const [currency, setCurrency] = useState('')
  /** The plan to seed this customer from. Empty = the deployment default preset
   *  (the historical behaviour). */
  const [planId, setPlanId] = useState('')
  const [plans, setPlans] = useState<Array<{ id: string; name: string }>>([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  /**
   * The preset this customer will be seeded from.
   *
   * `undefined` while unread (and if the read fails) — the line is then omitted
   * rather than guessing, because both possible guesses are wrong somewhere:
   * "free" is wrong on a configured deployment and a named preset is wrong on an
   * unconfigured one. `null` is the real, distinct state: nothing configured.
   */
  const [seedPreset, setSeedPreset] = useState<string | null | undefined>(undefined)

  useEffect(() => {
    let live = true
    void (async () => {
      try {
        const res = await budgetApi.getPlatformSettings()
        if (!live) return
        const id = res.settings.defaultPlanId
        const named = id ? res.plans.find(p => p.id === id) : null
        // A configured-but-missing pointer resolves the same way the server does
        // — back to free — so it reads as "nothing configured" rather than
        // naming a plan that is not there.
        setSeedPreset(named ? named.name : null)
        setPlans(res.plans.map(p => ({ id: p.id, name: p.name })))
      } catch {
        if (live) setSeedPreset(undefined)
      }
    })()
    return () => { live = false }
  }, [])

  useEffect(() => { nameRef.current?.focus() }, [])
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  // Owner email is OPTIONAL now: blank means "make me (the creating admin) the
  // owner". A NON-blank value must still be a valid address.
  const emailBlank = ownerEmail.trim() === ''
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(ownerEmail.trim())
  const canSubmit = !!name.trim() && (emailBlank || emailOk) && !saving

  const submit = async () => {
    if (!canSubmit) return
    setSaving(true)
    setError('')
    try {
      const res = await budgetApi.createTenant({
        name: name.trim(),
        // Blank owner ⇒ the route seats the CALLER as owner (no invite).
        ...(emailBlank ? {} : { ownerEmail: ownerEmail.trim() }),
        // Explicit plan to seed from; omitted = the deployment default preset.
        ...(planId ? { planId } : {}),
        // Caps are no longer set here — a new customer takes the plan's grants,
        // and any per-tenant ceiling is tuned on the Plan tab after creation.
        ...(currency ? { displayCurrency: currency } : {}),
      })
      if (res.warning) toast.error(res.warning)
      // Named, not inferred: the operator saw a preset promised on the form and
      // the server is the authority on which one was actually applied.
      if (res.seededFrom) {
        toast.success(t('settings.platform.newOrg.seeded', {
          defaultValue: 'Seeded from {{preset}}.', preset: res.seededFrom,
        }))
      }
      toast.success(
        res.owner === 'seated'
          ? t('settings.platform.newOrg.doneSeated', {
              defaultValue: '{{name}} created. {{email}} is the owner.',
              name: res.name, email: res.ownerEmail,
            })
          : res.inviteEmailSent
            ? t('settings.platform.newOrg.doneInvited', {
                defaultValue: '{{name}} created. An owner invite was emailed to {{email}}.',
                name: res.name, email: res.ownerEmail,
              })
            // The invite ROW exists either way — only the mail failed, and the
            // People tab can resend it. Saying "invite sent" here would be a
            // lie the operator only discovers when the customer never appears.
            : t('settings.platform.newOrg.doneInviteUnsent', {
                defaultValue: '{{name}} created, but the invite email could not be sent. Resend it from the People tab.',
                name: res.name,
              }),
      )
      onCreated(res.tenantId)
    } catch (e: any) {
      setError(e?.message || 'Failed to create the organization')
    } finally {
      setSaving(false)
    }
  }

  return (
    // `dismissible={!saving}`: provisioning creates a tenant AND sends an
    // invite, so a dialog dismissed mid-request would leave an operator unsure
    // whether an email had gone out.
    <Modal
      onClose={onClose}
      dismissible={!saving}
      title={t('settings.platform.newOrg.title', { defaultValue: 'New organization' })}
      description={t('settings.platform.newOrg.descBase', {
        defaultValue:
          'Creates the tenant and puts its owner in. Change their plan later from the tenant\'s Plan & billing tab.',
      })}
      footer={
        <>
          <button
            onClick={onClose}
            disabled={saving}
            className="px-3 py-1.5 text-xs rounded themed-btn-secondary disabled:opacity-50"
          >
            {t('common:cancel')}
          </button>
          <button
            onClick={submit}
            disabled={!canSubmit}
            className="px-3 py-1.5 text-xs rounded themed-btn-primary disabled:opacity-50"
          >
            {saving
              ? t('common:saving')
              : t('settings.platform.newOrg.create', { defaultValue: 'Create organization' })}
          </button>
        </>
      }
    >
        <div className="px-5 pt-3">
          {seedPreset !== undefined && (
            <p className={`text-xs mt-1 ${seedPreset ? 'themed-text-muted' : 'text-amber-300/90'}`}>
              {seedPreset
                ? t('settings.platform.newOrg.seedsFrom', {
                    defaultValue: 'Seeded from the {{preset}} preset — its caps, models and pricing.',
                    preset: seedPreset,
                  })
                : t('settings.platform.newOrg.seedsFromNone', {
                    defaultValue:
                      'No default preset is configured, so this tenant starts on the free plan — usually a zero AI budget. Set one in Platform settings.',
                  })}
            </p>
          )}
        </div>

        <div className="px-5 py-4 space-y-4">
          <Field
            label={t('settings.platform.newOrg.name', { defaultValue: 'Organization name' })}
            hint={t('settings.platform.newOrg.nameHint', { defaultValue: 'What the tenant calls themselves.' })}
          >
            <input
              ref={nameRef}
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={100}
              className="themed-input border rounded px-2 py-1.5 text-sm w-full outline-none focus:border-blue-500"
            />
          </Field>

          <Field
            label={t('settings.platform.newOrg.ownerOptional', { defaultValue: 'Owner email (optional)' })}
            hint={
              emailBlank
                ? t('settings.platform.newOrg.ownerHintBlank', {
                    defaultValue: 'Leave blank to make yourself the owner. Enter an address to invite someone else.',
                  })
                : t('settings.platform.newOrg.ownerHint', {
                    defaultValue:
                      'If this address already has an account they become the owner immediately. If not, they get an invite and set their own password — no account is created for them.',
                  })
            }
          >
            <input
              value={ownerEmail}
              onChange={(e) => setOwnerEmail(e.target.value)}
              type="email"
              autoComplete="off"
              className={`themed-input border rounded px-2 py-1.5 text-sm w-full outline-none focus:border-blue-500 ${
                ownerEmail.trim() && !emailOk ? 'border-red-500' : ''
              }`}
            />
          </Field>

          {plans.length > 0 && (
            <Field
              label={t('settings.platform.newOrg.plan', { defaultValue: 'Plan' })}
              hint={t('settings.platform.newOrg.planHint', {
                defaultValue: 'The plan to start this customer on. Leave on the default to use the deployment preset.',
              })}
            >
              <select
                value={planId}
                onChange={(e) => setPlanId(e.target.value)}
                className="themed-input border rounded px-2 py-1.5 text-sm w-full outline-none focus:border-blue-500"
              >
                <option value="">
                  {seedPreset
                    ? t('settings.platform.newOrg.planDefaultNamed', { defaultValue: 'Default ({{preset}})', preset: seedPreset })
                    : t('settings.platform.newOrg.planDefault', { defaultValue: 'Deployment default' })}
                </option>
                {plans.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </Field>
          )}

          <div className="pt-1 border-t themed-border">
            <div className="mt-3">
              <label className="themed-text-secondary text-[11px] mb-1 flex items-center gap-1">
                {t('settings.platform.newOrg.currency', { defaultValue: 'Display currency' })}
                <InfoHint text={t('settings.platform.newOrg.currencyHint', {
                  defaultValue:
                    'Display only — spend is still metered in US dollars. The rate follows the credit rate, so it can never disagree with what the tenant paid.',
                })} />
              </label>
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                className="themed-input border rounded px-2 py-1 text-xs outline-none focus:border-blue-500"
              >
                <option value="">
                  {t('settings.platform.newOrg.currencyDefault', { defaultValue: 'US dollars (default)' })}
                </option>
                {SUPPORTED_DISPLAY_CURRENCIES.filter((code) => code !== 'USD').map((code) => (
                  <option key={code} value={code}>{code}</option>
                ))}
              </select>
            </div>
          </div>

          {error && <p className="text-xs text-red-400">{error}</p>}
        </div>

    </Modal>
  )
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      {/* Helper text lives in a tooltip beside the label (redesign) — keeps the
          form uncluttered. */}
      <label className="themed-text-secondary text-xs mb-1 flex items-center gap-1">
        {label}
        {hint && <InfoHint text={hint} />}
      </label>
      {children}
    </div>
  )
}
